import { OrderSearchErrors } from "@redotech/customer-portal-sdk/rpc/schema/get-orders-from-search";
import { SendEmailAuthLinkError } from "@redotech/customer-portal-sdk/rpc/schema/send-email-auth-link";
import { SingleOrderAuthError } from "@redotech/customer-portal-sdk/rpc/schema/single-order-auth";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { getResource } from "@redotech/redo-model/localization/resource";
import { ReturnType, ReturnTypeEnum } from "@redotech/redo-model/return";
import {
  ReturnOption,
  teamOffersExchanges,
} from "@redotech/redo-model/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-model/return-flow/return-option";
import { Settings } from "@redotech/redo-model/team";

export enum HeaderText {
  RETURNS_ONLY = "Returns",
  CLAIMS_ONLY = "Claims",
  RETURNS_EXCHANGES = "Returns & Exchanges",
  RETURNS_CLAIMS = "Returns & Claims",
  RETURNS_CLAIMS_WARRANTIES = "Returns, Claims, & Warranties",
  RETURNS_WARRANTIES = "Returns & Warranties",
  WARRANTIES_ONLY = "Warranties",
}

export const orderSearchErrorToText: Record<OrderSearchErrors, string> = {
  [OrderSearchErrors.NO_EMAIL]: "No email found for this account",
  [OrderSearchErrors.INVALID_SEARCH]: "Invalid search parameters provided",
  [OrderSearchErrors.NEW_AUTH_ONLY]:
    "This endpoint may only be used with new auth",
  [OrderSearchErrors.ORDERLESS_UNSUPPORTED]:
    "Orderless auth scope is not supported",
  [OrderSearchErrors.INTERNAL_ERROR]:
    "Something went wrong on our end, please try again.",
};

export const singleOrderAuthErrorToText: Record<SingleOrderAuthError, string> =
  {
    [SingleOrderAuthError.INVALID_EMAIL_OR_ZIP]:
      "Invalid email/zip/order-number/tracking-number",
  };
export const sendEmailAuthLinkErrorToText: Record<
  SendEmailAuthLinkError,
  string
> = {
  [SendEmailAuthLinkError.InvalidEmail]:
    "The email we tried to send to is either inactive or marked as spam, please try again with a different email or contact support to update your email.",
  [SendEmailAuthLinkError.UnknownError]:
    "Something went wrong sending the email.",
};
export const ButtonText = (settings: ReturnAppSettings) => {
  return {
    RETURNS_ONLY: getResource({
      variant: "return",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    CLAIMS_ONLY: getResource({
      variant: "claim",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    RETURNS_EXCHANGES: getResource({
      variant: "exchange",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
    RETURNS_CLAIMS: getResource({
      variant: "return_claim",
      overrides: settings.resourceOverride,
    }).PORTAL_BUTTON_TEXT,
  };
};

const teamOffersPackageProtection = (
  settings: ReturnAppSettings | undefined,
): boolean => {
  return (
    (settings?.packageProtection?.enabled &&
      settings.packageProtection?.customerClaimsEnabled) === true
  );
};
const teamOffersReturns = (
  settings: ReturnAppSettings | undefined,
): boolean => {
  return !!settings?.returns?.enabled;
};

const teamOffersWarranties = (
  settings: ReturnAppSettings | undefined,
): boolean => {
  return !!settings?.warranties?.enabled;
};

export const getMissingOrderHeaderText = (settings: ReturnAppSettings) => {
  return settings.returnPortalMissingOrderHeaderText ?? getHeaderText(settings);
};

export const getHeaderText = (settings: ReturnAppSettings | undefined) => {
  if (settings?.returnPortalHeaderText) {
    return settings.returnPortalHeaderText;
  }

  const returns = teamOffersReturns(settings);
  const exchanges = teamOffersExchanges(settings?.returnFlow as any);
  const packageProtection = teamOffersPackageProtection(settings);
  const warranties = teamOffersWarranties(settings);
  // Ordering is important
  if (returns && packageProtection && warranties) {
    return HeaderText.RETURNS_CLAIMS_WARRANTIES;
  }
  if (returns && packageProtection) {
    return HeaderText.RETURNS_CLAIMS;
  }
  if (returns && exchanges) {
    return HeaderText.RETURNS_EXCHANGES;
  }
  if (returns && warranties) {
    return HeaderText.RETURNS_WARRANTIES;
  }
  if (packageProtection) {
    return HeaderText.CLAIMS_ONLY;
  }
  if (warranties) {
    return HeaderText.WARRANTIES_ONLY;
  }

  return HeaderText.RETURNS_ONLY;
};

export const getLoginButtonText = (settings: Settings) => {
  if (settings.returnPortalButtonText) {
    return settings.returnPortalButtonText;
  }
  if (
    settings.warranties?.registrationEnabled &&
    settings.warranties?.customersCanStartClaims
  ) {
    return "Search";
  }
  return "Find order";
};

export const getCantFindOrderText = (settings: ReturnAppSettings) => {
  return settings.theme?.cantFindOrderText ?? "Can't find order number?";
};

export const getStartReturnButtonText = (settings: ReturnAppSettings) => {
  if (settings.theme?.returnButtonText) {
    return settings.theme.returnButtonText;
  }
  if (teamOffersExchanges(settings?.returnFlow)) {
    return ButtonText(settings).RETURNS_EXCHANGES;
  }

  return ButtonText(settings).RETURNS_ONLY;
};

export const getPolicyLinkText = (settings: ReturnAppSettings | undefined) => {
  const returns = teamOffersReturns(settings);
  const packageProtection = teamOffersPackageProtection(settings);
  const variant =
    returns && packageProtection
      ? "return_claim"
      : packageProtection
        ? "claim"
        : "return";

  return getResource({ overrides: settings?.resourceOverride, variant })
    .PORTAL_POLICY_LINK_TEXT;
};

export const getWarrantyText = (settings: ReturnAppSettings | undefined) => {
  if (teamOffersWarranties(settings)) {
    return getResource({ overrides: settings?.resourceOverride })
      .PORTAL_WARRANTY_LINK_TEXT;
  }
  return "";
};

export const getLoginGiftText = (settings: ReturnAppSettings | undefined) => {
  if (settings?.returnPortalGiftButtonText) {
    return settings.returnPortalGiftButtonText;
  }
  const returns = teamOffersReturns(settings);
  const packageProtection = teamOffersPackageProtection(settings);
  const variant =
    returns && packageProtection
      ? "return_claim"
      : packageProtection
        ? "claim"
        : "return";

  return getResource({ overrides: settings?.resourceOverride, variant })
    .PORTAL_GIFT_BUTTON_TEXT;
};

function getReturnOptionBools(returnOptions: ReturnOption[]): {
  exchange: boolean;
  refund: boolean;
  credit: boolean;
} {
  let exchange = false;
  let refund = false;
  let credit = false;

  for (const option of returnOptions) {
    if (option.method.type === ReturnOptionMethod.EXCHANGE) {
      exchange = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.STORE_CREDIT) {
      credit = true;
      continue;
    }
    if (option.method.type === ReturnOptionMethod.REFUND) {
      refund = true;
      continue;
    }
  }
  return { exchange, refund, credit };
}

export function getGroupedReturnOptionsButtonText(
  returnOptions: ReturnOption[],
): string {
  const { exchange, refund, credit } = getReturnOptionBools(returnOptions);

  let str = exchange ? "Exchange for" : "Return for";
  let delim = "";

  if (exchange) {
    str += " a new product";
    delim = "or get";
  }
  if (refund) {
    str += ` ${delim} a refund`;
    delim = "or";
  }
  if (credit && (!refund || !exchange)) {
    str += ` ${delim} store credit`;
  }
  // Remove double spaces
  str = str.replace(/\s+/g, " ");
  return str;
}

export function getSummarySubtext(
  settings: ReturnAppSettings,
  paymentOwed: number,
  freeNewOrderShipping: boolean,
  totalStoreCredit: number,
  totalRefund: number,
  returnType: ReturnType,
) {
  if (
    returnType === ReturnTypeEnum.CLAIM &&
    settings.theme?.claimSummarySubtext
  ) {
    return settings.theme.claimSummarySubtext;
  }
  if (
    returnType === ReturnTypeEnum.RETURN &&
    settings.theme?.returnSummarySubtext
  ) {
    return settings.theme.returnSummarySubtext;
  }
  if (
    returnType === ReturnTypeEnum.WARRANTY &&
    settings.theme?.warrantySummarySubtext
  ) {
    return settings.theme.warrantySummarySubtext;
  }

  let refundText = `Once your ${returnType} is processed`;

  if (paymentOwed > 0 || !freeNewOrderShipping) {
    refundText += ", you will receive an email to pay for ";
    if (paymentOwed <= 0) {
      refundText += "the shipping for ";
    }
    refundText += "your new order";
  } else if (totalStoreCredit > 0) {
    refundText += ", you will receive an email with your store credit";
  } else if (totalRefund > 0) {
    refundText += ", you will receive a refund to your original payment method";
  }
  refundText += ".";

  return refundText;
}
